import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, Plus, Save, X, Upload, FileCheck2, FileX2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { CATEGORIES } from "@/lib/data";
import { toast } from "sonner";

const empty = { category: "disiplin", title: "", description: "", url: "#", active: true };

export default function AdminResources() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploadingId, setUploadingId] = useState(null);
  const fileRefs = useRef({});

  const load = async () => {
    const { data } = await api.get("/admin/resources");
    setItems(data.items || []);
  };

  useEffect(() => {
    if (!localStorage.getItem("life101_admin_token")) { nav("/admin/login"); return; }
    load();
  }, [nav]);

  const save = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Başlık ve açıklama zorunlu");
      return;
    }
    try {
      if (editing) {
        await api.put(`/admin/resources/${editing}`, form);
        toast.success("Kaynak güncellendi");
      } else {
        await api.post("/admin/resources", form);
        toast.success("Kaynak eklendi");
      }
      setEditing(null);
      setForm(empty);
      load();
    } catch {
      toast.error("İşlem başarısız");
    }
  };

  const startEdit = (r) => {
    setEditing(r.id);
    setForm({ category: r.category, title: r.title, description: r.description, url: r.url, active: r.active });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Bu kaynağı silmek istediğine emin misin?")) return;
    await api.delete(`/admin/resources/${id}`);
    toast.success("Silindi");
    load();
  };

  const uploadFile = async (rid, file) => {
    if (!file) return;
    setUploadingId(rid);
    const fd = new FormData();
    fd.append("file", file);
    const token = localStorage.getItem("life101_admin_token");
    try {
      const res = await fetch(`${API}/admin/resources/${rid}/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Upload failed");
      }
      toast.success("Dosya yüklendi");
      load();
    } catch (e) {
      toast.error(e.message || "Yükleme başarısız");
    } finally {
      setUploadingId(null);
    }
  };

  const removeFile = async (rid) => {
    if (!window.confirm("Yüklü dosyayı kaldırmak istediğine emin misin?")) return;
    await api.delete(`/admin/resources/${rid}/upload`);
    toast.success("Dosya kaldırıldı");
    load();
  };

  return (
    <div className="min-h-screen text-white" style={{ background: "#050505" }} data-testid="admin-resources-page">
      <header className="max-w-7xl mx-auto px-6 py-7 flex items-center justify-between">
        <Link to="/admin" className="text-sm text-neutral-400 hover:text-white inline-flex items-center gap-2"><ArrowLeft size={16} /> Dashboard</Link>
        <h1 className="text-xl font-semibold">Kaynak Yönetimi</h1>
      </header>

      <main className="max-w-7xl mx-auto px-6 pb-16 space-y-8">
        <div className="glass-solid p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold">{editing ? "Kaynağı Düzenle" : "Yeni Kaynak Ekle"}</h2>
            {editing && <button onClick={() => { setEditing(null); setForm(empty); }} className="text-neutral-400 hover:text-white inline-flex items-center gap-1 text-sm"><X size={14} /> İptal</button>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Kategori</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-white py-3 outline-none focus:border-white" data-testid="resource-category">
                {CATEGORIES.map((c) => <option key={c.key} value={c.key} className="bg-black">{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Bağlantı (URL)</label>
              <input className="life-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." data-testid="resource-url" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Başlık</label>
              <input className="life-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="resource-title" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Açıklama</label>
              <input className="life-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="resource-description" />
            </div>
            <label className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Aktif
            </label>
          </div>
          <button onClick={save} className="btn-primary mt-6 inline-flex items-center gap-2" data-testid="resource-save">
            {editing ? <><Save size={16} /> Güncelle</> : <><Plus size={16} /> Ekle</>}
          </button>
        </div>

        <div className="glass-solid overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-neutral-500 text-xs uppercase tracking-[0.12em]">
                <th className="p-4">Kategori</th>
                <th className="p-4">Başlık</th>
                <th className="p-4">Dosya</th>
                <th className="p-4">Durum</th>
                <th className="p-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-white/5 align-top">
                  <td className="p-4 text-neutral-300">{r.category_label}</td>
                  <td className="p-4">
                    <div className="text-neutral-100">{r.title}</div>
                    <div className="text-neutral-500 text-xs mt-1 max-w-md">{r.description}</div>
                  </td>
                  <td className="p-4">
                    {r.pdf_path ? (
                      <div className="flex items-center gap-2 text-xs">
                        <FileCheck2 size={14} className="text-emerald-400" />
                        <span className="text-neutral-200 max-w-[140px] truncate">{r.pdf_filename}</span>
                        <button onClick={() => removeFile(r.id)} className="text-red-400 hover:text-red-300 ml-2" data-testid={`remove-file-${r.id}`}>Kaldır</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-xs text-neutral-500">
                        <FileX2 size={14} />
                        <span>Dosya yok</span>
                      </div>
                    )}
                    <div className="mt-2">
                      <input
                        ref={(el) => (fileRefs.current[r.id] = el)}
                        type="file"
                        accept="application/pdf,image/*"
                        className="hidden"
                        onChange={(e) => uploadFile(r.id, e.target.files?.[0])}
                        data-testid={`file-input-${r.id}`}
                      />
                      <button
                        onClick={() => fileRefs.current[r.id]?.click()}
                        disabled={uploadingId === r.id}
                        className="btn-secondary text-xs inline-flex items-center gap-1"
                        data-testid={`upload-file-${r.id}`}
                      >
                        <Upload size={12} /> {uploadingId === r.id ? "Yükleniyor..." : (r.pdf_path ? "Değiştir" : "Yükle")}
                      </button>
                    </div>
                  </td>
                  <td className="p-4">{r.active ? <span className="text-white">Aktif</span> : <span className="text-neutral-500">Pasif</span>}</td>
                  <td className="p-4 text-right space-x-3 whitespace-nowrap">
                    <button onClick={() => startEdit(r)} className="text-neutral-300 hover:text-white text-xs" data-testid={`edit-${r.id}`}>Düzenle</button>
                    <button onClick={() => remove(r.id)} className="text-red-400 hover:text-red-300 inline-flex items-center gap-1 text-xs" data-testid={`delete-${r.id}`}><Trash2 size={12} /> Sil</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
